import PratusInput from '../../../DesignSystems/New/PratusInput';
import PratusSelect from '../../../DesignSystems/New/PratusSelect';
import PratusTextArea from '../../../DesignSystems/New/PratusTextArea';
import { StylishNewCheckbox } from '../../../DesignSystems/New/StylishNewCheckbox';
import { StylishSwitcher } from '../../../DesignSystems/New/StylishSwitcher';
import React, { useState } from 'react';
import MaterialsTable, { MaterialRow } from './MaterialsTable';
import {
  GetHazmatResponse,
  IncidentInfoResponse,
  Material,
  useGetSafetyAnalysis,
  useUpdateSafetyAnalysis,
} from '../../hooks/useSafetyAnalysis';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { SafetyMessageDto } from '../../../../types/iapBaseTypes';
import {
  EnvironmentalHazard,
  EnvironmentalHazardOption,
  HAZMATClassification,
  HazmatOption,
  IncidentNeed,
  IncidentNeedOption,
  OtherHazard,
  OtherHazardOption,
  PhysicalHazard,
  PhysicalHazardOption,
  PotentialHazard,
  PotentialHazardOption,
  RequiredPPE,
  RequiredPPEOption,
} from './types';
import { useSafetyMessages } from '../../hooks/useSafetyMessages';
import {
  ENVIRONMENTAL_HAZARD_OPTIONS,
  HAZMAT_CLASSIFICATION_OPTIONS,
  INCIDENT_NEED_OPTIONS,
  OTHER_HAZARD_OPTIONS,
  PHYSICAL_HAZARD_OPTIONS,
  POTENTIAL_HAZARD_OPTIONS,
  REQUIRED_PPE_OPTIONS,
} from 'components/IAP/ICSConstants';

export type SafetyAnalysisFormState = {
  safetyOfficer: string | null;
  physicalHazards: PhysicalHazard[] | null;
  environmentalHazards: EnvironmentalHazard[] | null;
  otherHazards: OtherHazard[] | null;
  weatherConditions: {
    temp?: string | null;
    conditions?: string | null;
    wind?: string | null;
    tides?: string | null;
    seaState?: string | null;
    waterTemp?: string | null;
    forecast?: string | null;
  };
  safetyNotes: string | null;
  requiredPPE: RequiredPPE[] | null;
  notesOnPPE: string | null;
  hazmatClassification: HAZMATClassification[] | null;
  otherClassification: string | null;
  material: Material[] | null;
  potentialHazards: PotentialHazard[] | null;
  incidentNeeds: IncidentNeed[] | null;
  airMonitoringRequired: boolean;
  standardOperatingProcedures: string | null;
  decontaminationProcedures: string | null;
  medicalMonitoringRequired: boolean;
  medicalTreatmentAndTransportInPlace: boolean;
  emergencyProcedures: string | null;
};

type BuildInitialData = (
  hazmatData: GetHazmatResponse | undefined,
  icsInfoData: IncidentInfoResponse | undefined,
  safetyMessagesData: SafetyMessageDto[] | undefined
) => SafetyAnalysisFormState;
const buildInitialData: BuildInitialData = (
  hazmatData,
  icsInfoData,
  safetyMessagesData
) => ({
  safetyOfficer: safetyMessagesData?.[0]?.safetyOfficer ?? null,
  physicalHazards: icsInfoData?.physicalHazards ?? null,
  environmentalHazards: icsInfoData?.environmentalHazards ?? null,
  otherHazards: icsInfoData?.otherHazards ?? null,
  weatherConditions: icsInfoData?.weather ?? {
    temp: null,
    conditions: null,
    wind: null,
    tides: null,
    seaState: null,
    waterTemp: null,
    forecast: null,
  },
  safetyNotes: safetyMessagesData?.[0]?.comments ?? null,
  requiredPPE: icsInfoData?.requiredPpe ?? null,
  notesOnPPE: icsInfoData?.notesOnPpe ?? null,
  potentialHazards: icsInfoData?.potentialHazards ?? null,

  hazmatClassification: hazmatData?.classifications ?? null,
  otherClassification: hazmatData?.otherClassification ?? null,
  material: hazmatData?.material ?? null,
  incidentNeeds: hazmatData?.incidentNeeds ?? null,
  airMonitoringRequired: hazmatData?.airMonitoring ?? false,
  standardOperatingProcedures: hazmatData?.sops ?? null,
  decontaminationProcedures: hazmatData?.decontaminationProcedure ?? null,
  medicalMonitoringRequired: hazmatData?.medicalMonitoring ?? false,
  medicalTreatmentAndTransportInPlace: hazmatData?.medicalTreatment ?? false,
  emergencyProcedures: hazmatData?.emergencyProcedures ?? null,
  additionalProductInfo: null, // not yet implemented on this form
  hazardMonitoringComments: null, // not yet implemented on this form
  lelInstruments: null, // not yet implemented on this form
  o2Instruments: null, // not yet implemented on this form
  radiologicalInstruments: null, // not yet implemented on this form
  toxicityInstruments: null, // not yet implemented on this form
});

const SafetyAnalysis = () => {
  const [hazmatQuery, icsInfoQuery, safetyMessagesQuery] =
    useGetSafetyAnalysis();

  if (
    hazmatQuery.isLoading ||
    icsInfoQuery.isLoading ||
    safetyMessagesQuery.isLoading
  ) {
    return <div className="d-flex justify-content-center">Loading...</div>;
  }
  const initialState = buildInitialData(
    hazmatQuery.data,
    icsInfoQuery.data,
    safetyMessagesQuery.data
  );

  return (
    <SafetyAnalysisForm
      initialState={initialState}
      initialShowHazmat={Boolean(hazmatQuery.data)}
    />
  );
};

interface SafetyAnalysisFormProps {
  initialState: SafetyAnalysisFormState;
  initialShowHazmat: boolean;
}
const SafetyAnalysisForm = ({
  initialState,
  initialShowHazmat,
}: SafetyAnalysisFormProps) => {
  const [formState, setFormState] = useState(initialState);
  const {
    updateIncidentInfo,
    createSafetyMessage,
    updateSafetyMessage,
    postHazmats,
  } = useUpdateSafetyAnalysis(formState);
  const safetyMessagesQuery = useSafetyMessages();
  const [showHazmat, setShowHazmat] = useState(initialShowHazmat);
  const isSaving =
    updateIncidentInfo.isPending ||
    postHazmats.isPending ||
    createSafetyMessage.isPending ||
    updateSafetyMessage.isPending;
  const safetyMessageId = safetyMessagesQuery.data?.[0]?.id;

  return (
    <div className="safety-analysis-container d-flex flex-column gap-3">
      <PratusInput
        label="Safety Officer"
        placeholder="Enter a safety officer..."
        value={formState.safetyOfficer ?? undefined}
        onValueChange={(value) =>
          setFormState((prev) => ({ ...prev, safetyOfficer: value }))
        }
      />
      <PratusSelect
        label="Physical Hazards"
        placeholder="Click to select physical hazards..."
        options={PHYSICAL_HAZARD_OPTIONS}
        value={PHYSICAL_HAZARD_OPTIONS.filter((option) =>
          formState.physicalHazards?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as PhysicalHazardOption[] | null)?.map((v) => v.value) ?? [];
          setFormState((prev) => ({
            ...prev,
            physicalHazards: newValues,
          }));
        }}
        isMulti
      />
      <PratusSelect
        label="Environmental Hazards"
        placeholder="Click to select environmental hazards..."
        options={ENVIRONMENTAL_HAZARD_OPTIONS}
        value={ENVIRONMENTAL_HAZARD_OPTIONS.filter((option) =>
          formState.environmentalHazards?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as EnvironmentalHazardOption[] | null)?.map(
              (v) => v.value
            ) ?? [];
          setFormState((prev) => ({
            ...prev,
            environmentalHazards: newValues,
          }));
        }}
        isMulti
      />
      <PratusSelect
        label="Other Hazards"
        placeholder="Click to select other hazards..."
        options={OTHER_HAZARD_OPTIONS}
        value={OTHER_HAZARD_OPTIONS.filter((option) =>
          formState.otherHazards?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as OtherHazardOption[] | null)?.map((v) => v.value) ?? [];
          setFormState((prev) => ({
            ...prev,
            otherHazards: newValues,
          }));
        }}
        isMulti
      />
      <div className="d-flex flex-column gap-2 weather-conditions">
        <>Weather Conditions:</>
        <div className="d-flex flex-row gap-1 flex-wrap">
          <PratusInput
            label="Temp:"
            placeholder="Enter temp..."
            value={formState.weatherConditions.temp ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: { ...prev.weatherConditions, temp: value },
              }))
            }
          />
          <PratusInput
            label="Conditions:"
            placeholder="Enter conditions..."
            value={formState.weatherConditions.conditions ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: {
                  ...prev.weatherConditions,
                  conditions: value,
                },
              }))
            }
          />
          <PratusInput
            label="Wind:"
            placeholder="Enter wind..."
            value={formState.weatherConditions.wind ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: { ...prev.weatherConditions, wind: value },
              }))
            }
          />
          <PratusInput
            label="Tides:"
            placeholder="Enter tides..."
            value={formState.weatherConditions.tides ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: { ...prev.weatherConditions, tides: value },
              }))
            }
          />
          <PratusInput
            label="Sea State:"
            placeholder="Enter sea..."
            value={formState.weatherConditions.seaState ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: {
                  ...prev.weatherConditions,
                  seaState: value,
                },
              }))
            }
          />
          <PratusInput
            label="Water Temp:"
            placeholder="Enter water..."
            value={formState.weatherConditions.waterTemp ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: {
                  ...prev.weatherConditions,
                  waterTemp: value,
                },
              }))
            }
          />
          <PratusInput
            label="Forecast:"
            placeholder="Enter forecast..."
            value={formState.weatherConditions.forecast ?? undefined}
            onValueChange={(value) =>
              setFormState((prev) => ({
                ...prev,
                weatherConditions: {
                  ...prev.weatherConditions,
                  forecast: value,
                },
              }))
            }
          />
        </div>
      </div>
      <PratusTextArea
        label="Safety Notes"
        placeholder="Enter a description..."
        value={formState.safetyNotes ?? undefined}
        onSave={(value) =>
          setFormState((prev) => ({ ...prev, safetyNotes: value }))
        }
        rows={10}
      />
      <PratusSelect
        label="Required Personal Protective Equipment (PPE)"
        placeholder="Click to select PPE..."
        options={REQUIRED_PPE_OPTIONS}
        value={REQUIRED_PPE_OPTIONS.filter((option) =>
          formState.requiredPPE?.includes(option.value)
        )}
        onChange={(value) => {
          const newValues =
            (value as RequiredPPEOption[] | null)?.map((v) => v.value) ?? [];
          setFormState((prev) => ({ ...prev, requiredPPE: newValues }));
        }}
        isMulti
      />
      <PratusTextArea
        label="Notes on PPE"
        placeholder="Enter a description..."
        value={formState.notesOnPPE ?? undefined}
        onSave={(value) =>
          setFormState((prev) => ({ ...prev, notesOnPPE: value }))
        }
      />
      <div className="d-flex flex-row align-items-center gap-2 hazmat-row">
        <StylishSwitcher
          className="hazmat-switch"
          value="Does the incident involve Hazardous Materials (HAZMAT)?"
          checked={showHazmat}
          onChange={() => setShowHazmat(!showHazmat)}
        />
        <>Does the incident involve Hazardous Materials (HAZMAT)?</>
      </div>

      <div
        className={`hazmat-container gap-2 flex-column ${showHazmat ? 'd-flex' : 'd-none'}`}
      >
        <PratusSelect
          label="HAZMAT Classification"
          placeholder="Click to select hazardous materials..."
          options={HAZMAT_CLASSIFICATION_OPTIONS}
          value={HAZMAT_CLASSIFICATION_OPTIONS.filter((option) =>
            formState.hazmatClassification?.includes(option.value)
          )}
          onChange={(value) => {
            const newValues =
              (value as HazmatOption[] | null)?.map((v) => v.value) ?? [];
            setFormState((prev) => ({
              ...prev,
              hazmatClassification: newValues,
            }));
          }}
          isMulti
        />
        {formState.hazmatClassification?.includes('Other/Miscellaneous') && (
          <PratusTextArea
            label="Other/Miscellaneous HAZMAT Classifications"
            placeholder="Enter a classification..."
            value={formState.otherClassification ?? undefined}
            onSave={(value) =>
              setFormState((prev) => ({
                ...prev,
                otherClassification: value,
              }))
            }
            rows={2}
          />
        )}
        <div className="mt-2">
          <MaterialsTable
            value={formState.material}
            onChange={(rows: MaterialRow[]) => {
              setFormState((prev) => ({ ...prev, material: rows }));
            }}
          />
        </div>
        <PratusSelect
          label="Potential Hazards"
          placeholder="Click to select potential hazards..."
          options={POTENTIAL_HAZARD_OPTIONS}
          value={POTENTIAL_HAZARD_OPTIONS.filter((option) =>
            formState.potentialHazards?.includes(option.value)
          )}
          onChange={(value) => {
            const newValues =
              (value as PotentialHazardOption[] | null)?.map((v) => v.value) ??
              [];
            setFormState((prev) => ({ ...prev, potentialHazards: newValues }));
          }}
          isMulti
        />
        <PratusSelect
          label="Does the incident require a need for any of the following?"
          placeholder="Click to select required information..."
          options={INCIDENT_NEED_OPTIONS}
          value={INCIDENT_NEED_OPTIONS.filter((option) =>
            formState.incidentNeeds?.includes(option.value)
          )}
          onChange={(value) => {
            const newValues =
              (value as IncidentNeedOption[] | null)?.map((v) => v.value) ?? [];
            setFormState((prev) => ({ ...prev, incidentNeeds: newValues }));
          }}
          isMulti
        />
        <StylishNewCheckbox
          label="Air Monitoring Required?"
          checked={formState.airMonitoringRequired}
          onClick={() =>
            setFormState((prev) => ({
              ...prev,
              airMonitoringRequired: !prev.airMonitoringRequired,
            }))
          }
        />
        <PratusTextArea
          label="Standard Operating Procedures (SOP) and Safe Work Practices"
          placeholder="Enter a description..."
          value={formState.standardOperatingProcedures ?? undefined}
          onSave={(value) =>
            setFormState((prev) => ({
              ...prev,
              standardOperatingProcedures: value,
            }))
          }
          rows={10}
        />
        <PratusTextArea
          label="Decontamination Procedures:"
          placeholder="Enter a description..."
          value={formState.decontaminationProcedures ?? undefined}
          onSave={(value) =>
            setFormState((prev) => ({
              ...prev,
              decontaminationProcedures: value,
            }))
          }
          rows={10}
        />
        <div className="d-flex flex-row align-items-left gap-4 medical-row">
          <>Medical:</>
          <StylishNewCheckbox
            label="Medical Monitoring Required?"
            checked={formState.medicalMonitoringRequired}
            onClick={() =>
              setFormState((prev) => ({
                ...prev,
                medicalMonitoringRequired: !prev.medicalMonitoringRequired,
              }))
            }
          />
          <StylishNewCheckbox
            label="Medical Treatment and Transport in place?"
            checked={formState.medicalTreatmentAndTransportInPlace}
            onClick={() =>
              setFormState((prev) => ({
                ...prev,
                medicalTreatmentAndTransportInPlace:
                  !prev.medicalTreatmentAndTransportInPlace,
              }))
            }
          />
        </div>
        <PratusTextArea
          label="Emergency Procedures:"
          placeholder="Enter a description..."
          value={formState.emergencyProcedures ?? undefined}
          onSave={(value) =>
            setFormState((prev) => ({ ...prev, emergencyProcedures: value }))
          }
          rows={10}
        />
      </div>
      <div className="mt-3 d-flex justify-content-end">
        <StylishNewButton
          onClick={() => {
            updateIncidentInfo.mutate();
            postHazmats.mutate();
            if (safetyMessageId) {
              updateSafetyMessage.mutate(safetyMessageId);
            } else {
              createSafetyMessage.mutate();
            }
          }}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Complete and Continue'}
        </StylishNewButton>
      </div>
    </div>
  );
};

export default SafetyAnalysis;
